import { Link } from 'react-router-dom'
import { Section, SectionHeading, FAQ, CTABand, SplitHero, NonMedicalNote } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const areas = [
  { title: 'Dementia Care', blurb: 'Calm routines, gentle cueing and familiar faces that reduce confusion at home.', href: '/specialized-care/dementia' },
  { title: "Alzheimer's Care", blurb: 'Consistent caregivers, memory-friendly days and steady family communication.', href: '/specialized-care/alzheimers' },
  { title: 'Palliative Care Support', blurb: 'Non-medical comfort, presence and household help alongside your palliative team.', href: '/specialized-care/palliative' },
  { title: 'End-of-Life Care Support', blurb: 'Quiet companionship and practical help so families can simply be together.', href: '/specialized-care/end-of-life' },
  { title: 'Respite Care', blurb: 'Planned or short-notice relief so family caregivers can rest and recharge.', href: '/specialized-care/respite' },
]

const pillarHighlights = [
  { title: 'Consistency of Caregivers', body: "For dementia and Alzheimer's care, familiarity is everything. We prioritize building a small, dedicated team of caregivers so your loved one always sees a familiar face." },
  { title: 'Family Communication', body: 'Specialized care can be stressful for families. Our portal and regular check-ins ensure you are never in the dark about how your loved one is doing.' },
]

const faqs = [
  { q: 'How do you match caregivers for specialized needs?', a: "We look for caregivers with specific experience in dementia or palliative support, ensuring their temperament and skills align with your family's unique situation." },
  { q: 'Can specialized plans change as needs evolve?', a: 'Absolutely. We review specialized care plans frequently. As conditions change, we adjust the level of support and types of tasks to ensure safety and comfort.' },
]

export default function SpecializedCare() {
  return (
    <>
      <SplitHero
        eyebrow="Services"
        title="Compassionate care for specialized needs"
        lede="When care needs become more complex, we provide the steady, familiar presence and specialized support required to maintain dignity and safety at home."
        image="/assets/specialized.jpg"
        imageAlt="A caregiver providing gentle support to a senior"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Schedule an In-Home Assessment</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Areas of specialized support */}
      <Section tone="white">
        <SectionHeading
          title="Areas of specialized support"
          lede="We provide non-medical assistance across a range of complex needs, always working alongside your medical team."
        />
        <div className="grid grid-3" style={{ marginTop: 48, gap: 24 }}>
          {areas.map((a) => (
            <div key={a.title} className="card-cream">
              <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{a.title}</h3>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)', flex: 1 }}>{a.blurb}</p>
              <Link to={a.href} className="link-inline" style={{ marginTop: 24 }}>View service details</Link>
            </div>
          ))}
        </div>
      </Section>

      {/* 5-Pillar Care Plan */}
      <Section tone="panel">
        <SectionHeading
          title="Built on the 5-Pillar Care Plan"
          lede="Every specialized client receives a custom care plan that covers more than just tasks—it covers the whole person."
        />
        <div style={{ marginTop: 32 }}>
          <Link to="/how-it-works" className="btn btn-primary">Learn about our 5-Pillar Plan</Link>
        </div>
        <div className="grid grid-2" style={{ marginTop: 64, gap: 32 }}>
          {pillarHighlights.map((p) => (
            <div key={p.title} style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{p.title}</h3>
              <p style={{ margin: '16px 0 0 0', fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Non-medical disclaimer */}
      <Section tone="white">
        <NonMedicalNote />
      </Section>

      {/* FAQ */}
      <Section tone="cream">
        <FAQ items={faqs} />
      </Section>

      <CTABand />
    </>
  )
}
